import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, ShoppingCart, TrendingUp, Filter, Clock, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

function HistoriqueAchats() {
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreMagasin, setFiltreMagasin] = useState('tous');
  const [filtreCategorie, setFiltreCategorie] = useState('tous');
  const [vueType, setVueType] = useState('semaine'); // 'semaine' ou 'mois'
  const [magasins, setMagasins] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    chargerHistorique();
  }, [filtreMagasin, filtreCategorie, vueType]);

  const chargerHistorique = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtreMagasin !== 'tous') params.append('magasin', filtreMagasin);
      if (filtreCategorie !== 'tous') params.append('categorie', filtreCategorie);
      params.append('groupe_par', vueType); // 'semaine' ou 'mois'
      
      const response = await axios.get(`/achats/historique?${params.toString()}`);
      
      if (response.data.success) {
        setHistorique(vueType === 'semaine' ? response.data.historique_par_semaine : response.data.historique_par_mois);
        setMagasins(response.data.magasins_disponibles || []);
        setCategories(response.data.categories_disponibles || []);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const formaterPeriode = (debut, fin) => {
    const optionsDate = { day: 'numeric', month: 'short' };
    const dateDebut = new Date(debut).toLocaleDateString('fr-FR', optionsDate);
    const dateFin = new Date(fin).toLocaleDateString('fr-FR', optionsDate);
    return `${dateDebut} - ${dateFin}`;
  };

  const formatDateDisplay = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toCSVSafe = (value) => {
    const text = value == null ? '' : String(value);
    if (text.includes(';') || text.includes('"') || text.includes('\n')) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const exporterCSV = () => {
    const lignes = [];
    lignes.push(['Periode', 'Date achat', 'Type', 'Produit', 'Categorie', 'Magasin', 'Quantite', 'Prix (€)']);

    historique.forEach((periode) => {
      const periodeLabel = vueType === 'semaine'
        ? `Semaine ${periode.numero_semaine || '-'}`
        : (periode.nom_mois || '-');

      (periode.achats || []).forEach((achat) => {
        const achatType = (achat.type || 'ACHAT').toString().toUpperCase();
        const prixNum = Number(achat.prix);
        lignes.push([
          periodeLabel,
          achat.date_achat || '',
          achatType,
          achat.produit_nom || achat.nom || 'Produit',
          achat.categorie || 'autre',
          achat.magasin || 'Non specifie',
          achat.quantite || '',
          Number.isFinite(prixNum) ? prixNum.toFixed(2) : '0.00'
        ]);
      });
    });

    if (lignes.length <= 1) {
      toast.info('Aucune donnée à exporter avec les filtres actuels');
      return;
    }

    const csvContent = lignes.map((row) => row.map(toCSVSafe).join(';')).join('\n');
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateTag = new Date().toISOString().slice(0, 10);
    const scope = vueType === 'semaine' ? 'hebdo' : 'mensuel';
    link.href = url;
    link.download = `historique-achats-${scope}-${dateTag}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Export CSV téléchargé');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête et filtres */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center space-x-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              <span>Historique des Achats</span>
            </CardTitle>
            <Button onClick={exporterCSV} variant="outline" size="sm">
              📥 Exporter CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Toggle Semaine/Mois */}
            <div className="flex items-center justify-center gap-2 bg-gray-100 p-1 rounded-lg w-fit mx-auto">
              <Button
                variant={vueType === 'semaine' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setVueType('semaine')}
                className="flex items-center space-x-1"
              >
                <Clock size={14} />
                <span>Par Semaine</span>
              </Button>
              <Button
                variant={vueType === 'mois' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setVueType('mois')}
                className="flex items-center space-x-1"
              >
                <CalendarDays size={14} />
                <span>Par Mois</span>
              </Button>
            </div>

            {/* Filtres */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2 flex-1">
                <Filter size={16} className="text-gray-500" />
                <Select value={filtreMagasin} onValueChange={setFiltreMagasin}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les magasins" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Tous les magasins</SelectItem>
                    {magasins.map(magasin => (
                      <SelectItem key={magasin} value={magasin}>{magasin}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 flex-1">
                <Filter size={16} className="text-gray-500" />
                <Select value={filtreCategorie} onValueChange={setFiltreCategorie}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes catégories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Toutes catégories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des semaines */}
      {historique.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun achat enregistré</p>
            <p className="text-xs text-gray-400 mt-2">
              Scannez des produits et confirmez leur prix pour voir l'historique
            </p>
          </CardContent>
        </Card>
      ) : (
        historique.map((periode, index) => {
          // Calculer les statistiques par catégorie
          const statsCategories = {};
          periode.achats.forEach(achat => {
            const cat = achat.categorie || 'Autre';
            if (!statsCategories[cat]) {
              statsCategories[cat] = { nombre: 0, total: 0 };
            }
            statsCategories[cat].nombre += 1;
            statsCategories[cat].total += achat.prix;
          });

          return (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {vueType === 'semaine' ? (
                      <Calendar className="w-5 h-5 text-blue-600" />
                    ) : (
                      <CalendarDays className="w-5 h-5 text-blue-600" />
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {vueType === 'semaine' 
                          ? `Semaine ${periode.numero_semaine}` 
                          : periode.nom_mois}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {formaterPeriode(periode.date_debut, periode.date_fin)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-1">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-2xl font-bold text-green-600">
                        {periode.total_depense.toFixed(2)} €
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {periode.nombre_achats} achat{periode.nombre_achats > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Statistiques par catégorie */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(statsCategories).map(([cat, stats]) => (
                    <div
                      key={cat}
                      className="bg-white px-3 py-1 rounded-full text-xs flex items-center space-x-2 border"
                    >
                      <span className="font-medium text-gray-700">{cat}</span>
                      <span className="text-gray-500">
                        {stats.nombre} × {stats.total.toFixed(2)}€
                      </span>
                    </div>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  {periode.achats.map((achat, idx) => {
                    const mouvementType = (achat.type || 'ACHAT').toString().toUpperCase();
                    const isConsumption = mouvementType === 'CONSOMMATION' || mouvementType === 'VENTE' || mouvementType === 'SORTIE';
                    const prixNum = Number(achat.prix);
                    const safePrix = Number.isFinite(prixNum) ? prixNum : 0;

                    // Icône de catégorie
                    const getCategoryIcon = (cat) => {
                      const catLower = (cat || '').toLowerCase();
                      if (catLower.includes('aliment') || catLower.includes('nourriture')) return '🍽️';
                      if (catLower.includes('boisson')) return '🥤';
                      if (catLower.includes('hygiène') || catLower.includes('soin')) return '🧴';
                      if (catLower.includes('ménage') || catLower.includes('entretien')) return '🧹';
                      if (catLower.includes('bébé') || catLower.includes('enfant')) return '👶';
                      return '📦';
                    };

                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">{getCategoryIcon(achat.categorie)}</span>
                            <span className="font-medium text-gray-900">
                              {achat.produit_nom}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                              isConsumption ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {isConsumption ? 'CONSOMMATION' : 'ACHAT'}
                            </span>
                            {achat.quantite && (
                              <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                                {achat.quantite}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-3 mt-2">
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                              🏬 {achat.magasin}
                            </span>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">
                              {achat.categorie || 'Autre'}
                            </span>
                            <span className="text-xs text-gray-400">
                              📅 {formatDateDisplay(achat.date_achat)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <span className={`text-xl font-bold ${isConsumption ? 'text-orange-600' : 'text-green-600'}`}>
                            {safePrix.toFixed(2)} €
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}

export default HistoriqueAchats;
